import { motion } from 'motion/react'
import React from 'react'
import { useNavigate, Link, useLocation } from 'react-router-dom'

import { paths } from 'src/config/paths'
import { VerificationBadge } from 'src/shared/components/icons/verification-badge'
import { NumberTicker } from 'src/shared/components/ui/animation/text-ticker'
import { Button } from 'src/shared/components/ui/buttons/button'
import { ProgressBar } from 'src/shared/components/ui/progress-bar'
import type { PrivateUserData } from 'src/shared/features/users/schemas/user'
import { cn } from 'src/shared/utils/cn'

interface PercentSectionProps {
  user: PrivateUserData
  variant?: 'profile' | 'sidebar'
  className?: string
}

export const PercentSection: React.FC<PercentSectionProps> = ({ user, variant = 'profile', className }) => {
  const navigate = useNavigate()
  const location = useLocation()
  if (!user.missingProfileFields?.length) {
    return null
  }

  const content = (
    <div className={cn('flex flex-col', variant === 'profile' && 'gap-4', variant === 'sidebar' && 'gap-2')}>
      <div className={cn('flex flex-row justify-between', variant === 'profile' ? 'items-start' : 'items-center')}>
        <div>
          <h3 className={cn('font-bold', variant === 'profile' && 'text-2xl')}>Verify Your Profile</h3>
          <div
            className={cn(
              'mb-1 font-bold text-points',
              variant === 'profile' && 'text-3xl',
              variant === 'sidebar' && 'text-xl',
            )}
          >
            {variant === 'profile' ? (
              <span>
                <NumberTicker value={user.profileComplete} className="text-points" />%
              </span>
            ) : (
              `${user.profileComplete}%`
            )}
          </div>
        </div>
        <div className={cn('relative', variant === 'profile' ? 'mr-4' : 'mr-2')}>
          <div
            className={cn(
              '-rotate-6 overflow-hidden rounded-xl bg-muted/20 outline outline-background',
              variant === 'profile' && 'h-16 w-16',
              variant === 'sidebar' && 'h-10 w-10',
            )}
          >
            {user.profilePhoto ? (
              <img src={user.profilePhoto} alt="Profile" className="size-full object-cover" />
            ) : (
              <div className="size-full" />
            )}
          </div>
          <div
            className={cn(
              'absolute -bottom-4 -right-4',
              variant === 'profile' && 'scale-100',
              variant === 'sidebar' && 'scale-75',
            )}
          >
            <VerificationBadge isVerified={true} className="size-8" />
          </div>
        </div>
      </div>

      {variant === 'profile' && (
        <>
          <ProgressBar value={user.profileComplete} delay={0.2} />
          <p className="mt-4 text-lg text-muted">
            No shortcuts - complete these fields to officially be a part of the network.
          </p>
          <Button
            onClick={() => navigate(paths.users.completeProfile.getHref(location.pathname))}
            variant="default"
            className="whitespace-nowrap"
          >
            Make it official
          </Button>
        </>
      )}
    </div>
  )

  if (variant === 'sidebar') {
    return (
      <motion.div whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }} style={{ transformOrigin: 'center' }}>
        <Link
          to={paths.users.completeProfile.getHref(location.pathname)}
          className={cn('block cursor-pointer rounded-3xl border bg-accent px-4 py-2.5', className)}
        >
          {content}
        </Link>
      </motion.div>
    )
  }

  return <div className={cn('relative flex flex-col rounded-lg border bg-accent p-6', className)}>{content}</div>
}

export default PercentSection
